import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { Eyebrow, PrimaryBtn, GhostBtn, H2 } from "@/components/public/PublicLayout";
import { AssessmentTiers } from "@/components/public/AssessmentTiers";
import { C, SERIF } from "@/lib/publicLinks";
import { imgUrl } from "@/context/CartContext";
import { DEFAULT_ABOUT, useSiteContent } from "@/lib/siteContent";
import { DIRECTED_TREATMENTS, splitClinical } from "@/lib/serviceCategories";

const Wrap = ({ children, tint = false, id, testid }) => (
  <section id={id} style={{ background: tint ? C.lightBg : "#fff" }} data-testid={testid}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">{children}</div>
  </section>
);

// Internal paths use the router; anything with a hash/absolute URL uses <a>.
const Go = ({ to, children, ghost = false, ...p }) => {
  const external = /^https?:\/\//.test(to || "");
  const Btn = ghost ? GhostBtn : PrimaryBtn;
  if (external) return <Btn href={to} {...p}>{children}</Btn>;
  if ((to || "").includes("#")) {
    return <a href={to} className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition ${ghost ? "bg-white" : "text-white"}`}
      style={ghost ? { color: C.primary, border: `1px solid ${C.line}` } : { background: C.primary }} {...p}>{children}</a>;
  }
  return <Btn to={to} {...p}>{children}</Btn>;
};

export const HeroSection = ({ s }) => (
  <section style={{ background: C.plum }} className="text-white" data-testid="home-hero">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
      <div>
        <Eyebrow dark>{s.eyebrow}</Eyebrow>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" style={SERIF} data-testid="home-h1">
          {s.headline} <span className="block mt-2 italic" style={{ color: C.lavender }}>{s.headlineAccent}</span>
        </h1>
        <p className="mt-6 text-base sm:text-lg leading-relaxed max-w-xl" style={{ color: "#DCD3E6" }}>{s.subcopy}</p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <PrimaryBtn to={s.primaryTo} className="!bg-white !text-[#2E1245]" data-testid="home-cta-primary">{s.primaryLabel} <ArrowRight className="h-4 w-4" /></PrimaryBtn>
          <a href={s.secondaryTo} className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white" style={{ border: "1px solid rgba(255,255,255,0.4)" }} data-testid="home-cta-secondary">{s.secondaryLabel}</a>
        </div>
        <p className="mt-6 text-xs tracking-wide" style={{ color: "#B9A8CE" }}>{s.note}</p>
      </div>
      <div className="rounded-3xl overflow-hidden shadow-2xl" style={{ aspectRatio: "5/4", border: "1px solid rgba(201,205,211,0.35)" }}>
        <img src={imgUrl(s.image)} alt="Dr. Christy Vonta Bell at the KISSED clinic with a trichology microscope" className="w-full h-full object-cover" data-testid="home-hero-image" />
      </div>
    </div>
  </section>
);

export const TrustSection = ({ s }) => {
  const about = useSiteContent("about", DEFAULT_ABOUT);
  const f = about.founder || DEFAULT_ABOUT.founder;
  const creds = ((about.credentials || DEFAULT_ABOUT.credentials).items || []).filter((c) => c.visible !== false && c.label);
  return (
    <Wrap testid="home-trust">
      <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-10 items-center">
        <div className="rounded-3xl overflow-hidden max-w-sm mx-auto lg:mx-0" style={{ aspectRatio: "4/5" }}>
          <img src={imgUrl(s.image)} alt="Dr. Christy Vonta Bell" className="w-full h-full object-cover object-top" loading="lazy" data-testid="home-practitioner-image" />
        </div>
        <div>
          <Eyebrow>{s.eyebrow}</Eyebrow>
          <H2>{(f.kicker || "").replace(/^Meet\s+/i, "")}</H2>
          <p className="mt-2 text-sm font-medium" style={{ color: C.accent }}>{f.name}</p>
          <p className="text-sm" style={{ color: C.accent }}>{f.subtitle}</p>
          <p className="mt-5 text-slate-600 leading-relaxed max-w-2xl" data-testid="home-bio">{(f.paragraphs || [])[0]}</p>
          <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2" data-testid="home-credentials">
            {creds.map((c) => <li key={c.label} className="flex items-start gap-2 text-sm text-slate-700"><Check className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: C.green }} />{c.label}</li>)}
          </ul>
          <div className="mt-7"><GhostBtn to={s.aboutTo} data-testid="home-about-link">{s.aboutLabel} <ArrowRight className="h-4 w-4" /></GhostBtn></div>
        </div>
      </div>
    </Wrap>
  );
};

export const ConcernsSection = ({ s }) => (
  <Wrap tint testid="home-concerns">
    <Eyebrow>{s.eyebrow}</Eyebrow><H2>{s.heading}</H2>
    <p className="mt-3 text-slate-600 max-w-2xl">{s.sub}</p>
    <div className="mt-10 grid md:grid-cols-2 gap-5">
      {(s.items || []).map((c, i) => (
        <div key={c.t} className="rounded-2xl bg-white p-7 flex flex-col" style={{ border: `1px solid ${C.line}` }} data-testid={`home-concern-${i}`}>
          <div className="text-xl text-slate-900" style={SERIF}>{c.t}</div>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{c.d}</p>
          <div className="mt-5"><Go to={c.to} ghost data-testid={`home-concern-cta-${i}`}>{c.btn} <ArrowRight className="h-4 w-4" /></Go></div>
        </div>
      ))}
    </div>
  </Wrap>
);

export const HowSection = ({ s }) => (
  <Wrap testid="home-how">
    <Eyebrow>{s.eyebrow}</Eyebrow><H2 className="max-w-3xl">{s.heading}</H2>
    <ol className="mt-10 grid md:grid-cols-5 gap-4">
      {(s.steps || []).map((st, i) => (
        <li key={st.t} className="rounded-2xl p-5" style={{ border: `1px solid ${C.line}` }} data-testid={`home-step-${i}`}>
          <div className="text-3xl" style={{ ...SERIF, color: C.accent }}>{String(i + 1).padStart(2, "0")}</div>
          <div className="mt-3 font-semibold text-slate-900 text-sm">{st.t}</div>
          <p className="mt-2 text-sm text-slate-600">{st.d}</p>
        </li>
      ))}
    </ol>
    <p className="mt-6 text-sm text-slate-600 max-w-4xl rounded-xl px-5 py-4" style={{ background: C.lightBg }} data-testid="home-how-note">{s.note}</p>
  </Wrap>
);

export const TiersSection = ({ s }) => (
  <Wrap tint id="assessments" testid="home-tiers">
    <Eyebrow>{s.eyebrow}</Eyebrow><H2 className="max-w-3xl">{s.heading}</H2>
    <p className="mt-3 text-slate-600 max-w-2xl">{s.sub}</p>
    <div className="mt-10"><AssessmentTiers who={s.who} compact /></div>
    <p className="mt-6 text-sm font-medium" style={{ color: C.primary }} data-testid="home-tiers-note">{s.note}</p>
  </Wrap>
);

const useOffers = () => {
  const [offers, setOffers] = useState([]);
  useEffect(() => { api.get("/clinic-offers?type=clinical_service").then(({ data }) => setOffers(data.offers || [])).catch(() => {}); }, []);
  return offers;
};

export const InClinicSection = ({ s }) => {
  const { assessments, wellness } = splitClinical(useOffers());
  const groups = [
    { t: "Hair & Scalp Assessments", d: "Publicly bookable initial assessments for new clients.", list: assessments },
    { t: "General Wellness Services", d: "In-clinic wellness services and consultations.", list: wellness },
  ];
  return (
    <Wrap testid="home-inclinic">
      <div className="grid lg:grid-cols-[1fr_1fr] gap-10 items-start">
        <div>
          <Eyebrow>{s.eyebrow}</Eyebrow><H2>{s.heading}</H2>
          <p className="mt-3 text-slate-600">{s.sub}</p>
          <div className="mt-6 text-sm font-semibold text-slate-900">What an in-clinic assessment visit can include</div>
          <ul className="mt-3 space-y-2">{(s.visit || []).map((v) => <li key={v} className="flex items-start gap-2 text-sm text-slate-700"><Check className="h-4 w-4 mt-0.5" style={{ color: C.green }} />{v}</li>)}</ul>
          {s.hsaNote && <p className="mt-5 text-xs text-slate-500" data-testid="home-hsa-note">{s.hsaNote}</p>}
          <div className="mt-7"><GhostBtn to={s.allTo} data-testid="home-inclinic-all">{s.allLabel} <ArrowRight className="h-4 w-4" /></GhostBtn></div>
        </div>
        <div className="space-y-4">
          {groups.map((g, gi) => (
            <div key={g.t} className="rounded-2xl p-6" style={{ border: `1px solid ${C.line}` }} data-testid={`home-inclinic-group-${gi}`}>
              <div className="text-lg text-slate-900" style={SERIF}>{g.t}</div>
              <p className="mt-1 text-sm text-slate-600">{g.d}</p>
              <ul className="mt-4 divide-y" style={{ borderColor: C.line }}>
                {g.list.map((o) => (
                  <li key={o.id} className="py-3 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium text-slate-800">{o.title}</span>
                    {o.square_appointment_url && <a href={o.square_appointment_url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold inline-flex items-center gap-1" style={{ color: C.primary }} data-testid={`home-book-${o.id}`}>Book <ArrowRight className="h-3.5 w-3.5" /></a>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="rounded-2xl p-6" style={{ background: C.lightBg }} data-testid="home-inclinic-directed">
            <div className="text-lg text-slate-900" style={SERIF}>{DIRECTED_TREATMENTS.title}</div>
            <p className="mt-1 text-sm text-slate-600">High Frequency, LLLT, microneedling and other scalp therapies are planned by your practitioner after an assessment — they are not booked independently.</p>
            <a href={DIRECTED_TREATMENTS.ctaTo} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: C.primary }} data-testid="home-directed-cta">{DIRECTED_TREATMENTS.ctaLabel} <ArrowRight className="h-4 w-4" /></a>
          </div>
        </div>
      </div>
    </Wrap>
  );
};

export const ContinuingSection = ({ s }) => (
  <Wrap tint testid="home-continuing">
    <Eyebrow>{s.eyebrow}</Eyebrow><H2>{s.heading}</H2>
    <div className="mt-10 grid md:grid-cols-3 gap-5">
      {(s.items || []).map((it, i) => (
        <div key={it.t} className="rounded-2xl bg-white p-6 flex flex-col" style={{ border: `1px solid ${C.line}` }}>
          <div className="text-lg text-slate-900" style={SERIF}>{it.t}</div>
          <p className="mt-2 text-sm text-slate-600 flex-1">{it.d}</p>
          <Link to={it.to} className="mt-5 text-sm font-semibold inline-flex items-center gap-1" style={{ color: C.primary }} data-testid={`home-continuing-${i}`}>{it.btn} <ArrowRight className="h-4 w-4" /></Link>
        </div>
      ))}
    </div>
    <p className="mt-6 text-sm text-slate-600" data-testid="home-continuing-note">{s.note}</p>
  </Wrap>
);

export const FaqSection = ({ s }) => {
  const [open, setOpen] = useState(0);
  return (
    <Wrap testid="home-faq">
      <div className="max-w-3xl">
        <Eyebrow>{s.eyebrow}</Eyebrow><H2>{s.heading}</H2>
        <div className="mt-8 divide-y" style={{ borderTop: `1px solid ${C.line}`, borderBottom: `1px solid ${C.line}` }}>
          {(s.items || []).map((f, i) => (
            <div key={f.q} style={{ borderColor: C.line }}>
              <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full py-4 flex items-center justify-between text-left gap-4" data-testid={`home-faq-${i}`}>
                <span className="font-medium text-slate-900">{f.q}</span>
                <ChevronDown className={`h-4 w-4 flex-shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} style={{ color: C.accent }} />
              </button>
              {open === i && <p className="pb-5 text-sm text-slate-600 leading-relaxed" data-testid={`home-faq-answer-${i}`}>{f.a}</p>}
            </div>
          ))}
        </div>
      </div>
    </Wrap>
  );
};

export const ClosingSection = ({ s }) => (
  <section style={{ background: C.plum }} className="text-white" data-testid="home-closing">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
      <h2 className="text-3xl sm:text-4xl" style={SERIF}>{s.heading}</h2>
      <p className="mt-4" style={{ color: "#DCD3E6" }}>{s.copy}</p>
      <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
        <PrimaryBtn to={s.ctaTo} className="!bg-white !text-[#2E1245]" data-testid="home-closing-cta">{s.ctaLabel} <ArrowRight className="h-4 w-4" /></PrimaryBtn>
        <Link to={s.secondaryTo} className="inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold text-white" style={{ border: "1px solid rgba(255,255,255,0.4)" }} data-testid="home-closing-carehub">{s.secondaryLabel}</Link>
      </div>
    </div>
  </section>
);
